import dayjs from 'dayjs'
import { useContext, useEffect, useMemo, useState } from 'react'

import { API } from '@/api'
import { ServerDomainInfo, Volume } from '@/api/type-server'
import { Button, message, Modal, Trans } from '@/components'
import { AppContext } from '@/components/Layout'
import { VolumeType } from '@/consts/machine'
import DriveVolumeQuotaSection from '@/pages/space-mgr/components/drive-volume-quota-section.tsx'
import { useUserStore } from '@/stores/user'
import { toGbStrig } from '@/utils/file'
import { cn } from '@/utils/style'

import { EraseDataModal } from './erase-data-modal'
import VolumeQuotaSection from './volume-quota-section'
import { VolumeQuotaSegmentedBar } from './volume-quota-segmented-bar'

export interface SelectedData {
  selectedData: Array<SelectedDataItem>
  lastestVolume?: Volume
}

interface SelectedDataItem {
  volume: Volume
  selectedQuotaInGB: number
}

export default function VolumeQuotaModal(props: {
  open: boolean
  data: {
    snapshotVolumes?: Volume[]
    cloudVolumes?: Volume[]
    domainInfos?: ServerDomainInfo[]
  }
  onClose: () => void
  onOk: () => void
}) {
  const { open, data, onClose, onOk } = props
  const { snapshotVolumes, cloudVolumes, domainInfos } = data || {}
  const userStore = useUserStore()
  const app = useContext(AppContext)

  const [loading, setLoading] = useState(false)
  const [eraseDataModalOpen, setEraseDataModalOpen] = useState(false)
  const [warningVolumeIds, setWarningVolumeIds] = useState<Set<number>>(new Set())

  const partVolumeQuotaInGB = userStore.partVolume?.quota_gb || 0

  const [summarizedData, setSummarizedData] = useState({
    selectedData: [...(snapshotVolumes || []), ...(cloudVolumes || [])].map((val) => {
      return {
        volume: val,
        selectedQuotaInGB: val.quota_gb || 0,
      } as SelectedDataItem
    }),
  } as SelectedData)

  const { availableInGB, hasModified, shouldDisableUpdate } = useMemo(() => {
    const { selectedData = [] } = summarizedData
    let selectedInGB = 0
    for (const item of selectedData) {
      selectedInGB += item.selectedQuotaInGB
    }

    const index = selectedData.findIndex((d) => d.volume.quota_gb !== d.selectedQuotaInGB)
    const hasModified = index !== -1

    // Check for warning conditions
    const newWarningVolumeIds = new Set<number>()
    let hasInsufficientQuota = false

    selectedData.forEach((item) => {
      const { volume, selectedQuotaInGB } = item
      const usedGB = Number(((volume.usage_mb || 0) / 1024).toFixed(2))

      // If quota is set to 0 and there's usage > 0, add to warning
      if (selectedQuotaInGB === 0 && usedGB > 0) {
        newWarningVolumeIds.add(volume.id)
      }

      // If quota is less than used space but not 0, mark as insufficient
      if (selectedQuotaInGB > 0 && selectedQuotaInGB < usedGB) {
        hasInsufficientQuota = true
      }
    })

    // Update states if changed
    if (
      newWarningVolumeIds.size !== warningVolumeIds.size ||
      [...newWarningVolumeIds].some((id) => !warningVolumeIds.has(id))
    ) {
      setWarningVolumeIds(newWarningVolumeIds)
    }

    // Button should be disabled if there are warning volumes (quota=0 with usage>0) OR insufficient quota
    const shouldDisableUpdate = newWarningVolumeIds.size > 0 || hasInsufficientQuota

    return {
      availableInGB: partVolumeQuotaInGB - selectedInGB,
      hasModified,
      shouldDisableUpdate,
    }
  }, [summarizedData, partVolumeQuotaInGB, warningVolumeIds])

  const onVolumeChange = (volume: Volume, value: number) => {
    const { selectedData = [] } = summarizedData
    const index = selectedData.findIndex((val) => val.volume.id === volume.id)

    if (index !== -1) {
      selectedData[index].selectedQuotaInGB = value
    } else {
      selectedData.push({
        volume: volume,
        selectedQuotaInGB: value,
      })
    }

    summarizedData.lastestVolume = volume
    setSummarizedData({ ...summarizedData, selectedData })
  }

  const onConfirm = async () => {
    // TODO: check if the new quota is less than the used space
    // const { selectedData = [] } = summarizedData
    // const modifiedVolumes = selectedData.filter((d) => d.volume.quota_gb !== d.selectedQuotaInGB)

    // const hasZeroQuotaDrive = modifiedVolumes.some(
    //   (d) => d.selectedQuotaInGB === 0 && d.volume.type === VolumeType.DataDrive,
    // )

    // const hasZeroQuotaSnapshot = modifiedVolumes.some(
    //   (d) => d.selectedQuotaInGB === 0 && d.volume.type === VolumeType.Snapshot,
    // )

    // if (hasZeroQuotaDrive || hasZeroQuotaSnapshot) {
    //   setEraseDataModalOpen(true)
    //   return
    // }

    await handleEraseDataConfirm()
  }

  const handleEraseDataConfirm = async () => {
    setEraseDataModalOpen(false)
    const { selectedData = [] } = summarizedData
    const modifiedVolumes = selectedData.filter((d) => d.volume.quota_gb !== d.selectedQuotaInGB)

    const requests = modifiedVolumes.map((i) => {
      if (i.volume.id < 0) {
        return API.createSubvolume({
          type: 1,
          domain: i.volume.domain || 0,
          init_quota_gb: i.selectedQuotaInGB,
        })
          .then((response) => {
            return { success: !response, response }
          })
          .catch((error) => ({ success: false, error }))
      } else {
        return API.modifySubvolume({
          id: i.volume.id,
          quota_gb: i.selectedQuotaInGB,
        })
          .then((response) => {
            return { success: !response, response }
          })
          .catch((error) => ({ success: false, error }))
      }
    })

    try {
      setLoading(true)
      const results = await Promise.all(requests)
      const successCount = results.filter((result) => result.success).length
      const totalCount = results.length

      if (successCount === totalCount) {
        message.success('Modification successful')
      } else if (successCount > 0) {
        message.show(`Partially successful: ${successCount}/${totalCount} operations succeeded`)
      }

      app.refresh()
      onOk()
    } catch (error) {
      console.error('Error during volume modifications:', error)
      message.error('Modification failed')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (open) {
      userStore.getOrder()
    }
  }, [open])

  return (
    <>
      <Modal
        className="w-[800px]"
        open={open}
        onClose={() => {
          if (!loading) {
            onClose()
          }
        }}
      >
        <Modal.Header>
          <Trans i18nKey="create.volumeQuotaModalTitle">Modify Quota</Trans>
        </Modal.Header>
        <Modal.Body>
          <div className="t-bold text-primary">
            <Trans i18nKey="space.titleSpaceStorage">Storage Capacity</Trans>
          </div>

          <div className="my-4">
            <VolumeQuotaSegmentedBar summarizedData={summarizedData} />
          </div>

          <table className="table">
            <tbody>
              <tr className="h-[44px]">
                <td className="w-[120px] text-secondary t-sm !px-0 !py-3" align="left">
                  <Trans i18nKey="space.volumeQuotaModel.storage">Storage</Trans>
                </td>
                <td
                  className={cn(
                    ' t-sm !px-0 !py-3',
                    partVolumeQuotaInGB ? 'text-primary' : 'text-tertiary',
                  )}
                >
                  {partVolumeQuotaInGB ? toGbStrig(partVolumeQuotaInGB) : '-'}
                </td>
              </tr>
              <tr className="h-[44px]">
                <td className="w-[120px] text-secondary t-sm !px-0 !py-3" align="left">
                  <Trans i18nKey="space.volumeQuotaModel.expireOn">Expire</Trans>
                </td>
                <td
                  className={cn(
                    't-sm !px-0 !py-3',
                    userStore.order ? 'text-primary' : 'text-tertiary',
                  )}
                >
                  {userStore.order
                    ? dayjs(userStore.order.deadlineTime.seconds * 1000).format('YYYY-MM-DD')
                    : '-'}
                </td>
              </tr>
            </tbody>
          </table>

          <div className="mt-4 mb-2 flex flex-col gap-8">
            {snapshotVolumes && (
              <VolumeQuotaSection
                isSnapshot={true}
                volumes={snapshotVolumes}
                availableInGB={availableInGB}
                summarizedData={summarizedData}
                onChange={onVolumeChange}
                warningVolumeIds={warningVolumeIds}
              />
            )}
            {cloudVolumes && (
              <DriveVolumeQuotaSection
                volumes={cloudVolumes}
                availableInGB={availableInGB}
                summarizedData={summarizedData}
                domainInfos={domainInfos}
                onChange={onVolumeChange}
                warningVolumeIds={warningVolumeIds}
              />
            )}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button
            type="outline"
            onClick={() => {
              if (!loading) {
                onClose()
              }
            }}
          >
            <Trans i18nKey="common.confirmModalCancel">Cancel</Trans>
          </Button>
          <Button
            type="secondary"
            disabled={loading || !hasModified || shouldDisableUpdate}
            onClick={onConfirm}
          >
            <Trans i18nKey="common.confirmModalUpdate">Update</Trans>
          </Button>
        </Modal.Footer>
      </Modal>
      <EraseDataModal
        open={eraseDataModalOpen}
        onClose={() => setEraseDataModalOpen(false)}
        onConfirm={handleEraseDataConfirm}
        domainNames={
          domainInfos
            ?.filter((domainInfo) => {
              const volume = [...(snapshotVolumes || []), ...(cloudVolumes || [])].find(
                (v) => v.domain === domainInfo.domain,
              )
              const selectedData = summarizedData.selectedData.find(
                (d) => d.volume.domain === domainInfo.domain,
              )
              return selectedData?.selectedQuotaInGB === 0 && volume?.quota_gb !== 0
            })
            .map((d) => d.displayName) || []
        }
        dataDriveConfig={(() => {
          const modifiedVolumes = summarizedData.selectedData.filter(
            (d) => d.volume.quota_gb !== d.selectedQuotaInGB,
          )
          return modifiedVolumes.some(
            (d) => d.selectedQuotaInGB === 0 && d.volume.type === VolumeType.DataDrive,
          )
            ? 0
            : 1
        })()}
        snapshotConfig={(() => {
          const modifiedVolumes = summarizedData.selectedData.filter(
            (d) => d.volume.quota_gb !== d.selectedQuotaInGB,
          )
          return modifiedVolumes.some(
            (d) => d.selectedQuotaInGB === 0 && d.volume.type === VolumeType.Snapshot,
          )
            ? 0
            : 1
        })()}
      />
    </>
  )
}
